import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { ApiError, apiGet } from "../../lib/api";
import { etatCleSchema } from "../contract";
import { genererRequete } from "../essai-obs";
import { formatTime } from "../regie-texts";
import { SignIn } from "../sign-in";
import { CadreReglages, PANNEAU, TITRE_PANNEAU } from "./cadre";
import { QrOverlay } from "./qr-overlay";
import {
  OVERLAYS,
  OVERLAYS_ADRESSES_NOTE,
  OVERLAYS_ADRESSES_TITRE,
  OVERLAYS_ANNULER,
  OVERLAYS_CHARGEMENT,
  OVERLAYS_CLE_ABSENTE,
  OVERLAYS_CLE_TITRE,
  OVERLAYS_CONFIRMATION,
  OVERLAYS_CONFIRMER,
  OVERLAYS_COPIE_REFUSEE,
  OVERLAYS_COPIEE,
  OVERLAYS_COPIER,
  OVERLAYS_GENERATION,
  OVERLAYS_GENERATION_ECHEC,
  OVERLAYS_GENERER,
  OVERLAYS_INTRO,
  OVERLAYS_LECTURE_ECHEC,
  OVERLAYS_REGENERER,
  OVERLAYS_TITRE,
  overlayAdresse,
  overlayAdresseLabel,
  overlayCopierLabel,
  overlaysCleDepuis,
} from "./reglages-texts";

/**
 * La page « Overlays d'OBS » (`/regie/reglages/overlays`) : l'etat de la cle d'overlay (`GET /api/direct/cle`), sa
 * generation (`POST /api/direct/cle`) et l'adresse complete de chaque overlay, `<origine>/overlay/<nom>#cle=<cle>`.
 *
 * **La cle n'est affichee qu'une fois**, juste apres la generation : la base n'en a que l'empreinte. Elle ne vit que
 * dans l'etat de ce composant, jamais dans le cache des requetes (`gcTime: 0` sur la mutation), ni dans le stockage du
 * navigateur, ni dans l'adresse de la page : recharger ou quitter la page l'efface. Regenerer remplace la cle,
 * coupe les flux des overlays ouverts et demande d'abord une confirmation.
 *
 * Sous la cle, le reglage du QR code du stream (`qr-overlay.tsx`) : son coin et son interrupteur.
 */

const CLE_KEY = ["direct", "cle"] as const;

type Copie = "copiee" | "refusee";

export function CleOverlay(): ReactElement {
  const queryClient = useQueryClient();
  // La cle en clair, montree une fois. Jamais relue, jamais gardee ailleurs que dans cet etat.
  const [cle, setCle] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [copies, setCopies] = useState<Record<string, Copie>>({});
  const champs = useRef<Record<string, HTMLInputElement | null>>({});

  const etat = useQuery({
    queryKey: CLE_KEY,
    queryFn: () => apiGet("/api/direct/cle", etatCleSchema),
    retry: false,
    gcTime: 0,
  });

  const generer = useMutation({
    mutationFn: genererRequete,
    gcTime: 0,
    onSuccess: (nouvelle) => {
      setConfirmation(false);
      setCopies({});
      setCle(nouvelle);
      void queryClient.invalidateQueries({ queryKey: CLE_KEY });
    },
  });

  function lancer(): void {
    generer.reset();
    setCle(null);
    setCopies({});
    generer.mutate();
  }

  async function copier(nom: string, adresse: string): Promise<void> {
    try {
      if (!navigator.clipboard) throw new Error("presse-papiers absent");
      await navigator.clipboard.writeText(adresse);
      setCopies((avant) => ({ ...avant, [nom]: "copiee" }));
    } catch {
      // Refuse (permission, page non securisee) : l'adresse est selectionnee, a copier a la main.
      champs.current[nom]?.select();
      setCopies((avant) => ({ ...avant, [nom]: "refusee" }));
    }
  }

  if (etat.isError && etat.error instanceof ApiError && [401, 403].includes(etat.error.status)) {
    return (
      <CadreReglages titre={OVERLAYS_TITRE}>
        <SignIn callbackURL="/regie/reglages/overlays" />
      </CadreReglages>
    );
  }

  const posee = etat.isSuccess && etat.data.posee;
  const depuis =
    etat.isSuccess && etat.data.creeeLe !== null ? formatTime(etat.data.creeeLe) : null;
  const origine = window.location.origin;

  return (
    <CadreReglages titre={OVERLAYS_TITRE}>
      <section className={PANNEAU} aria-labelledby="overlays-cle">
        <h2 id="overlays-cle" className={TITRE_PANNEAU}>
          {OVERLAYS_CLE_TITRE}
        </h2>
        <p className="m-0 text-muted-foreground">{OVERLAYS_INTRO}</p>

        {etat.isPending ? (
          <p className="m-0 text-muted-foreground">{OVERLAYS_CHARGEMENT}</p>
        ) : etat.isError ? (
          <p role="alert" className="m-0 text-destructive">
            {OVERLAYS_LECTURE_ECHEC}
          </p>
        ) : (
          <p className={posee ? "m-0" : "m-0 text-muted-foreground"}>
            {posee ? overlaysCleDepuis(depuis) : OVERLAYS_CLE_ABSENTE}
          </p>
        )}

        {cle === null ? null : (
          <div className="flex flex-col gap-3 rounded-[4px] border-2 border-[#E9A951] bg-background p-4">
            <h3 className="m-0 text-[22px] font-normal text-primary">{OVERLAYS_ADRESSES_TITRE}</h3>
            {OVERLAYS.map((overlay) => {
              const adresse = overlayAdresse(origine, overlay.chemin, cle);
              const idChamp = `overlay-adresse-${overlay.nom}`;
              return (
                <div key={overlay.chemin} className="flex flex-col gap-1.5">
                  <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[120px_minmax(0,1fr)_auto]">
                    <label htmlFor={idChamp} className="police-nom text-[28px] text-[#FCDD8E]">
                      {overlay.nom}
                    </label>
                    <input
                      id={idChamp}
                      ref={(element) => {
                        champs.current[overlay.nom] = element;
                      }}
                      readOnly
                      aria-label={overlayAdresseLabel(overlay.nom)}
                      value={adresse}
                      onFocus={(event) => event.currentTarget.select()}
                      className="w-full min-w-0 rounded-[3px] border-2 border-input bg-[var(--nuit)] px-3 py-2.5 font-mono text-[16px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <Button
                      size="md"
                      className="min-h-12 px-5 text-[22px]"
                      aria-label={overlayCopierLabel(overlay.nom)}
                      onClick={() => void copier(overlay.nom, adresse)}
                    >
                      {OVERLAYS_COPIER}
                    </Button>
                  </div>
                  {copies[overlay.nom] === undefined ? null : (
                    <p
                      role="status"
                      className={
                        copies[overlay.nom] === "copiee"
                          ? "m-0 text-[19px] text-[#7FD3A6]"
                          : "m-0 text-[19px] text-destructive"
                      }
                    >
                      {copies[overlay.nom] === "copiee" ? OVERLAYS_COPIEE : OVERLAYS_COPIE_REFUSEE}
                    </p>
                  )}
                </div>
              );
            })}
            <p className="m-0 text-[19px] text-muted-foreground">{OVERLAYS_ADRESSES_NOTE}</p>
          </div>
        )}

        {confirmation ? (
          <div className="flex flex-col items-start gap-3" role="alert">
            <p className="m-0">{OVERLAYS_CONFIRMATION}</p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                disabled={generer.isPending}
                className="min-h-12 text-[22px] text-destructive"
                onClick={lancer}
              >
                {OVERLAYS_CONFIRMER}
              </Button>
              <Button
                variant="outline"
                className="min-h-12 text-[22px]"
                onClick={() => setConfirmation(false)}
              >
                {OVERLAYS_ANNULER}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              variant={posee ? "outline" : "default"}
              disabled={generer.isPending || !etat.isSuccess}
              className={posee ? "min-h-12 text-[22px] text-destructive" : "min-h-12 text-[22px]"}
              onClick={() => (posee ? setConfirmation(true) : lancer())}
            >
              {posee ? OVERLAYS_REGENERER : OVERLAYS_GENERER}
            </Button>
          </div>
        )}
        {generer.isPending ? (
          <p role="status" className="m-0 text-muted-foreground">
            {OVERLAYS_GENERATION}
          </p>
        ) : generer.isError ? (
          <p role="alert" className="m-0 text-destructive">
            {OVERLAYS_GENERATION_ECHEC}
          </p>
        ) : null}
      </section>
      <QrOverlay />
    </CadreReglages>
  );
}
